import { Switch, SwitchProps } from '@chronosphereio/chrono-ui';
import { ErrorAlert } from '@/components';
import {
  AGGREGATION_OPTIONS,
  AGGREGATIONS_CONFIG,
  AggregationOption,
  DEFAULT_LEGEND,
  getLegendMode,
  getLegendPosition,
  getLegendSize,
  LEGEND_MODE_CONFIG,
  LEGEND_POSITIONS_CONFIG,
  LEGEND_SIZE_CONFIG,
  LegendSingleSelectConfig,
  LegendSpecOptions,
  validateLegendSpec,
} from '@/core';
import { OptionsEditorControl } from '../OptionsEditorLayout';
import { SettingsAutocomplete } from '../SettingsAutocomplete';

type LegendPositionOption = LegendSingleSelectConfig & { id: LegendSpecOptions['position'] };

const POSITION_OPTIONS: LegendPositionOption[] = Object.entries(LEGEND_POSITIONS_CONFIG).map(([id, config]) => {
  return {
    id: id as LegendSpecOptions['position'],
    ...config,
  };
});

type LegendModeOption = LegendSingleSelectConfig & { id: Required<LegendSpecOptions>['mode'] };

const MODE_OPTIONS: LegendModeOption[] = Object.entries(LEGEND_MODE_CONFIG).map(([id, config]) => {
  return {
    id: id as Required<LegendSpecOptions>['mode'],
    ...config,
  };
});

type LegendSizeOption = LegendSingleSelectConfig & { id: Required<LegendSpecOptions>['size'] };

const SIZE_OPTIONS: LegendSizeOption[] = Object.entries(LEGEND_SIZE_CONFIG).map(([id, config]) => {
  return {
    id: id as Required<LegendSpecOptions>['size'],
    ...config,
  };
});

export interface LegendOptionsEditorProps {
  value?: LegendSpecOptions;
  onChange: (legend?: LegendSpecOptions) => void;
  hideValuesDropdown?: boolean;
}

export function LegendOptionsEditor({ value, onChange, hideValuesDropdown }: LegendOptionsEditorProps) {
  const handleLegendShowChange: SwitchProps['onChange'] = (_: unknown, checked: boolean) => {
    // legend is hidden when legend obj is undefined
    const legendValue = checked === true ? { position: DEFAULT_LEGEND.position } : undefined;
    onChange(legendValue);
  };

  const handleLegendPositionChange = (_: unknown, newValue: LegendPositionOption) => {
    onChange({
      ...value,
      position: newValue.id,
    });
  };

  const handleLegendModeChange = (_: unknown, newValue: LegendModeOption) => {
    onChange({
      ...value,
      position: currentPosition,
      mode: newValue.id,
    });
  };

  const handleLegendSizeChange = (_: unknown, newValue: LegendSizeOption) => {
    onChange({
      ...value,
      position: currentPosition,
      size: newValue.id,
    });
  };

  const handleLegendValueChange = (_: unknown, newValue: AggregationOption[]) => {
    const nextValues = newValue.map((v) => v.id);
    const nextValuesSet = new Set<string>(nextValues);

    // Drop any sort entries that reference a calculation no longer in `values`.
    // Leaving an orphaned sort behind makes the runtime hand MUI DataGrid a
    // sortModel pointing at a missing column, which forces an expensive
    // full-row client-side sort. See specSortToGridSortModel for the runtime
    // counterpart that defends against pre-existing orphaned sorts.
    const filteredSort = value?.sort?.filter((s) => nextValuesSet.has(s.sort_by));
    const nextSort = filteredSort && filteredSort.length > 0 ? filteredSort : undefined;

    onChange({
      ...value,
      position: currentPosition,
      values: nextValues,
      sort: nextSort,
    });
  };

  const isValidLegend = validateLegendSpec(value);
  const currentPosition = getLegendPosition(value?.position);
  const legendPositionConfig = LEGEND_POSITIONS_CONFIG[currentPosition];

  const currentMode = getLegendMode(value?.mode);
  const legendModeConfig = LEGEND_MODE_CONFIG[currentMode];

  const currentSize = getLegendSize(value?.size);
  const legendSizeConfig = LEGEND_SIZE_CONFIG[currentSize];

  const currentValues = value?.values || [];
  const legendValuesConfig = currentValues.reduce((result, item) => {
    const config = AGGREGATIONS_CONFIG[item];
    if (config) {
      result.push({ ...config, id: item });
    }
    return result;
  }, [] as AggregationOption[]);

  return (
    <>
      {!isValidLegend && <ErrorAlert error={{ name: 'invalid-legend', message: 'Invalid legend spec' }} />}
      <OptionsEditorControl
        label="Show"
        control={<Switch checked={value !== undefined} onChange={handleLegendShowChange} />}
      />
      <OptionsEditorControl
        label="Position"
        control={
          <SettingsAutocomplete
            value={{
              ...legendPositionConfig,
              id: currentPosition,
            }}
            options={POSITION_OPTIONS}
            onChange={handleLegendPositionChange}
            disabled={value === undefined}
            disableClearable
          ></SettingsAutocomplete>
        }
      />
      <OptionsEditorControl
        label="Mode"
        control={
          <SettingsAutocomplete
            value={{
              ...legendModeConfig,
              id: currentMode,
            }}
            options={MODE_OPTIONS}
            onChange={handleLegendModeChange}
            disabled={value === undefined}
            disableClearable
          ></SettingsAutocomplete>
        }
      />
      <OptionsEditorControl
        label="Size"
        control={
          <SettingsAutocomplete
            value={{
              ...legendSizeConfig,
              id: currentSize,
            }}
            options={SIZE_OPTIONS}
            onChange={handleLegendSizeChange}
            disabled={value === undefined}
            disableClearable
          ></SettingsAutocomplete>
        }
      />
      {!hideValuesDropdown && (
        <OptionsEditorControl
          label="Values"
          control={
            // For some reason, the inferred option type doesn't always seem to work
            // quite right when `multiple` is true. Explicitly setting the generics
            // to work around this.
            <SettingsAutocomplete<AggregationOption, true, true>
              multiple={true}
              disableCloseOnSelect
              disableClearable
              value={legendValuesConfig}
              options={AGGREGATION_OPTIONS}
              onChange={handleLegendValueChange}
              disabled={value === undefined || currentMode !== 'Table'}
              limitTags={1}
              ChipProps={{
                size: 'small',
              }}
            />
          }
        />
      )}
    </>
  );
}
